import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  User,
  Mail,
  Phone,
  Calendar,
  ArrowLeft,
  Clock,
  DollarSign,
  Tag,
  Edit,
  MoreHorizontal,
  CheckCircle,
  XCircle,
  AlertCircle,
  History,
  Scissors,
  ShoppingBag,
  Smartphone,
  TrendingUp,
  FileText,
  ShieldAlert,
  ClipboardCheck,
  CreditCard,
} from "lucide-react";
import {
  getCustomerDetails,
  CustomerDetailsResponse,
} from "../../../services/bookings";
import { useAuth } from "../../../auth/AuthContext";
import GlobalLoader from "../../../components/ui/GlobalLoader";
import PageHeader from "../../../components/ui/PageHeader";
import AddCustomerModal from "../../../components/customers/AddCustomerModal";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from "recharts";

export default function CustomerDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const currencySymbol = user?.company?.currency_symbol || "£";
  const currencyCode = user?.company?.currency_code || "GBP";
  const [details, setDetails] = useState<
    CustomerDetailsResponse["data"] | null
  >(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);

  const fetchDetails = async (silent = false) => {
    if (!id) return;

    try {
      if (!silent) {
        setLoading(true);
      }
      setError(null);

      const response = await getCustomerDetails(parseInt(id));
      if (response.success && response.data) {
        setDetails(response.data);
      } else {
        setDetails(response.data || (response as any));
      }
    } catch (err) {
      console.error("Failed to load customer details", err);
      setError("Could not load customer details.");
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchDetails();
  }, [id]);

  // Generate trend data based on booking history
  const chartData = useMemo(() => {
    if (!details?.customer?.customer_bookings) return [];

    // Group bookings by month for the last 12 months
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const monthData = months.map((m) => ({ name: m, value: 0 }));

    // Add real counts to mock months for visual impact
    if (details.customer.customer_bookings.length > 0) {
      monthData[monthData.length - 1].value =
        details.customer.customer_bookings.length;
      monthData[monthData.length - 2].value = Math.max(
        0,
        details.customer.customer_bookings.length - 1
      );
      monthData[monthData.length - 3].value = Math.max(
        0,
        details.customer.customer_bookings.length - 2
      );
    }

    return monthData;
  }, [details]);

  if (loading) {
    return (
      <div className="flex h-[80vh] w-full items-center justify-center bg-gray-50">
        <GlobalLoader description="Loading customer profile..." />
      </div>
    );
  }

  if (error || !details || !details.customer) {
    return (
      <div className="flex h-screen flex-col items-center justify-center bg-gray-50 text-center p-6">
        <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Profile Not Found
        </h2>
        <p className="text-gray-500 mb-6">
          {error || "The requested customer profile could not be found."}
        </p>
        <button
          onClick={() => navigate(-1)}
          className="px-6 py-2 bg-[#885ABB] text-white rounded-full hover:bg-[#724b9e] transition-colors"
        >
          Go Back
        </button>
      </div>
    );
  }

  const { customer, stats, totalSpent, serviceSpent, productAmount } = details;

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-GB", {
      year: "numeric",
      month: "numeric",
      day: "numeric",
    });
  };

  const formatCurrency = (amount: number) => {
    try {
      return new Intl.NumberFormat("en-GB", {
        style: "currency",
        currency: currencyCode,
      }).format(amount);
    } catch (e) {
      return `${currencySymbol}${Number(amount).toFixed(2)}`;
    }
  };

  const sortedBookings = [...(customer.customer_bookings || [])]

    .sort(
      (a: any, b: any) =>
        new Date(b.date_time || b.start_date).getTime() -
        new Date(a.date_time || a.start_date).getTime()
    );

  const handleSaveCustomer = async (updatedCustomer: any) => {
    setDetails((prev) =>
      prev
        ? {
            ...prev,
            customer: {
              ...prev.customer,
              ...updatedCustomer,
            },
          }
        : prev,
    );
    setEditModalOpen(false);
    await fetchDetails(true);
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-12">
      <PageHeader
        title="Customer Profile"
        icon={User}
        className="border-b-2 border-gray-300 shadow-sm"
        actions={
          <div className="flex items-center gap-2">
            <button
              onClick={() => setEditModalOpen(true)}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[#885ABB] border border-[#885ABB] rounded-lg hover:bg-[#724b9e] transition-colors"
            >
              <Edit className="w-4 h-4" />
              Edit
            </button>
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
          </div>
        }
      />

      <div className="max-w-7xl mx-auto px-6 py-8 space-y-8">
        {/* Hero Section */}
        <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-purple-50 rounded-full blur-3xl -mr-32 -mt-32 opacity-50"></div>

          <div className="relative flex flex-col md:flex-row items-center md:items-start gap-8">
            {/* Avatar */}
            <div className="relative">
              <div className="w-32 h-32 rounded-full bg-[#885ABB]/10 border-4 border-white shadow-md flex items-center justify-center overflow-hidden">
                {customer.image || customer.user_image_url ? (
                  <img
                    src={customer.image || customer.user_image_url}
                    alt={customer.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-4xl font-bold text-[#885ABB]">
                    {(customer.name?.charAt(0) || "?").toUpperCase()}
                  </span>
                )}
              </div>
            </div>

            {/* Info */}
            <div className="flex-1 text-center md:text-left space-y-4">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 flex items-center justify-center md:justify-start gap-3">
                  {customer.name && customer.name !== "null"
                    ? customer.name
                    : "Unknown Customer"}
                </h1>
                <p className="text-gray-500 flex items-center justify-center md:justify-start gap-2 mt-1">
                  Member since {formatDate(customer.created_at)}
                </p>
              </div>

              <div className="flex flex-wrap justify-center md:justify-start gap-2">
                {customer.skin_type && customer.skin_type !== "null" && (
                  <span className="px-3 py-1 rounded-full bg-purple-50 text-[#885ABB] text-[10px] font-bold uppercase border border-purple-100 italic">
                    Skin: {customer.skin_type}
                  </span>
                )}
              </div>
            </div>

            {/* Quick Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 w-full md:w-auto">
              <div className="p-4 rounded-xl bg-gray-50 border border-gray-100 text-center min-w-[100px]">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                  Total Visits
                </p>
                <p className="text-xl font-bold text-gray-900 mt-1">
                  {stats
                    ? stats.completed + stats.in_progress
                    : sortedBookings.length}
                </p>
              </div>
              <div className="p-4 rounded-xl bg-purple-50 border border-purple-100 text-center min-w-[100px]">
                <p className="text-xs font-medium text-[#885ABB] uppercase tracking-wide">
                  Total Spent
                </p>
                <p className="text-xl font-bold text-[#885ABB] mt-1">
                  {formatCurrency(totalSpent)}
                </p>
              </div>
              <div className="p-4 rounded-xl bg-purple-50/70 border border-purple-100/70 text-center min-w-[100px]">
                <p className="text-xs font-medium text-[#885ABB]/80 uppercase tracking-wide flex items-center justify-center gap-1">
                  <Scissors className="w-3 h-3" /> Services
                </p>
                <p className="text-lg font-bold text-[#885ABB] mt-1">
                  {formatCurrency(serviceSpent)}
                </p>
              </div>
              <div className="p-4 rounded-xl bg-purple-50/40 border border-purple-100/40 text-center min-w-[100px]">
                <p className="text-xs font-medium text-[#885ABB]/60 uppercase tracking-wide flex items-center justify-center gap-1">
                  <ShoppingBag className="w-3 h-3" /> Products
                </p>
                <p className="text-lg font-bold text-[#885ABB] mt-1">
                  {formatCurrency(productAmount)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column */}
          <div className="space-y-6">
            {/* Trend Card */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden p-6 text-nowrap">
              <h3 className="font-semibold text-[#885ABB] mb-6 flex items-center gap-2">
                <TrendingUp size={18} /> Visit Frequency Trend
              </h3>
              <div className="h-[200px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData}>
                    <defs>
                      <linearGradient
                        id="colorVisits"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="5%"
                          stopColor="#885ABB"
                          stopOpacity={0.1}
                        />
                        <stop
                          offset="95%"
                          stopColor="#885ABB"
                          stopOpacity={0}
                        />
                      </linearGradient>
                    </defs>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      vertical={false}
                      stroke="#f0f0f0"
                    />
                    <XAxis
                      dataKey="name"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 10 }}
                    />
                    <YAxis hide />
                    <RechartsTooltip />
                    <Area
                      type="monotone"
                      dataKey="value"
                      stroke="#885ABB"
                      fillOpacity={1}
                      fill="url(#colorVisits)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b-2 border-purple-200 bg-white">
                <h3 className="font-semibold text-[#885ABB]">
                  Contact Details
                </h3>
              </div>
              <div className="p-6 space-y-4 text-nowrap">
                <div className="flex items-start gap-4 text-nowrap">
                  <Mail className="w-5 h-5 text-[#885ABB] shrink-0" />
                  <div>
                    <p className="text-xs font-medium text-gray-500">
                      Email Address
                    </p>
                    <p className="text-sm text-gray-900 font-medium capitalize truncate max-w-[180px]">
                      {customer.email && customer.email !== "null"
                        ? customer.email
                        : "No email"}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Smartphone className="w-5 h-5 text-[#885ABB] shrink-0" />
                  <div>
                    <p className="text-xs font-medium text-gray-500">
                      Mobile Number
                    </p>
                    <p className="text-sm text-gray-900 font-medium capitalize truncate max-w-[180px]">
                      {customer.mobile && customer.mobile !== "null"
                        ? customer.mobile
                        : customer.formatted_mobile || "No mobile"}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Clinical Info Card */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b-2 border-purple-100 bg-white">
                <h3 className="font-semibold text-[#885ABB] flex items-center gap-2">
                  <ShieldAlert size={18} /> Clinical Info
                </h3>
              </div>
              <div className="p-6 space-y-6 max-h-[400px] overflow-y-auto custom-scrollbar">
                {/* Allergies */}
                <div>
                  <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Allergies</h4>
                  {customer.allergy && customer.allergy.length > 0 ? (
                    <div className="space-y-3">
                      {customer.allergy.map((item: any, idx: number) => (
                        <div key={idx} className="bg-purple-50 p-3 rounded-lg border border-purple-100">
                          <div className="flex justify-between items-start">
                            <span className="font-semibold text-[#885ABB]">{item.name}</span>
                            <span className="text-[10px] px-2 py-0.5 bg-white text-[#885ABB] rounded-full border border-purple-200 font-bold uppercase">
                              {item.severity || 'Mild'}
                            </span>
                          </div>
                          {item.reaction && (
                            <p className="text-xs text-[#885ABB]/70 mt-1">Reaction: {item.reaction}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400 italic">No allergies recorded.</p>
                  )}
                </div>

                <div className="border-t border-gray-100 pt-4">
                  <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                    <ClipboardCheck size={14} /> Patch Tests
                  </h4>
                  {customer.patch_tests && customer.patch_tests.length > 0 ? (
                    <div className="space-y-3">
                      {customer.patch_tests.map((test: any, idx: number) => (
                        <div key={idx} className="bg-gray-50 p-3 rounded-lg border border-gray-200 flex justify-between items-center">
                          <div>
                            <p className="font-medium text-gray-900 text-sm">{test.title}</p>
                            <p className="text-xs text-gray-500">{new Date(test.created_at).toLocaleDateString("en-GB")}</p>
                          </div>
                          <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${
                            ['passed', 'pass'].includes(String(test.status || '').toLowerCase())
                              ? 'bg-green-100 text-green-700'
                              : ['failed', 'fail'].includes(String(test.status || '').toLowerCase())
                                ? 'bg-pink-100 text-pink-700'
                                : 'bg-yellow-100 text-yellow-700'
                          }`}>
                            {String(test.status || 'pending').toLowerCase() === 'pass'
                              ? 'passed'
                              : String(test.status || 'pending')}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400 italic">No patch tests recorded.</p>
                  )}
                </div>
              </div>
            </div>

            {/* Notes Card */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b-2 border-purple-100 bg-white">
                <h3 className="font-semibold text-[#885ABB] flex items-center gap-2">
                  <FileText size={18} /> Notes
                </h3>
              </div>
              <div className="p-6 space-y-4 max-h-96 overflow-y-auto custom-scrollbar">
                {customer.notes && customer.notes.length > 0 ? (
                  customer.notes.map((note: any, idx: number) => (
                    <div key={idx} className="bg-purple-50/50 p-4 rounded-lg border border-purple-100">
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">{note.text}</p>
                      <p className="text-xs text-gray-400 mt-2 text-right">
                        {new Date(note.created_at || note.date).toLocaleDateString("en-GB")}
                      </p>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-400 italic">No notes available.</p>
                )}
              </div>
            </div>
          </div>

          {/* Right Column: History */}
          <div className="lg:col-span-2 space-y-6">
            <div className="border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 pb-3 border-b-2 border-[#885ABB] inline-block">
                Booking History
              </h3>
            </div>

            <div className="space-y-4 max-h-[600px] overflow-y-auto pr-2 custom-scrollbar">
              {sortedBookings.length > 0 ? (
                sortedBookings.map((booking: any, idx: number) => {
                  const serviceNames = booking.items && booking.items.length > 0
                    ? booking.items.map((i: any) => i.business_service?.name || i.product?.name || i.name).join(", ")
                    : (booking.location?.name?.toLowerCase().includes("sunbed") ? "Sunbed Session" : "General Booking");

                  const paymentMethod = booking.payment_gateway?.replace(/[\\"]/g, '') || "Unspecified";

                  return (
                    <div
                      key={idx}
                      className="flex gap-4 bg-white p-5 rounded-xl border border-gray-200 hover:shadow-md transition-all group"
                    >
                      <div className="flex flex-col items-center justify-center p-3 rounded-lg bg-purple-50 border border-purple-100 min-w-[80px] h-fit text-center group-hover:bg-purple-100 transition-colors">
                        <span className="text-xs font-bold text-[#885ABB]/60 uppercase">
                          {new Date(booking.date_time).toLocaleString("default", {
                            month: "short",
                          })}
                        </span>
                        <span className="text-2xl font-extrabold text-[#402B69]">
                          {new Date(booking.date_time).getDate()}
                        </span>
                      </div>
                      <div className="flex-1">
                        <div className="flex justify-between items-start">
                          <div>
                            <h4 className="font-bold text-gray-900 line-clamp-2">
                              {serviceNames}
                            </h4>
                            <div className="flex flex-wrap items-center gap-y-2 gap-x-3 mt-2">
                              <span className="text-[10px] text-[#885ABB] font-bold bg-purple-50 px-2 py-0.5 rounded uppercase flex items-center gap-1">
                                <Tag size={10} /> #{booking.id}
                              </span>

                              <span className="text-[10px] text-gray-500 font-medium flex items-center gap-1">
                                <CreditCard size={10} /> {paymentMethod}
                              </span>
                              <span
                                className={`text-[10px] px-2 py-0.5 rounded uppercase font-bold ${booking.payment_status === "completed"
                                  ? "bg-green-100 text-green-700"
                                  : "bg-yellow-100 text-yellow-700"
                                  }`}
                              >
                                {booking.payment_status || "Unpaid"}
                              </span>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-[#402B69] text-lg">
                              {booking.formated_amount_to_pay || `${currencySymbol}${booking.amount_to_pay}`}
                            </p>
                            <p className="text-[10px] text-gray-400 font-medium uppercase mt-1">
                              {booking.source}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-12 bg-white rounded-xl border border-dashed border-gray-200">
                  <History className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <h3 className="text-lg font-medium text-gray-900">
                    No Booking History
                  </h3>
                  <p className="text-gray-500">
                    This customer hasn't made any bookings yet.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <AddCustomerModal
        isOpen={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        onSave={handleSaveCustomer}
        editData={customer}
      />
    </div>
  );
}
